import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Bot,
  Check,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  Compass,
  CreditCard,
  Gauge,
  Globe2,
  MapPin,
  Menu,
  MousePointer2,
  Play,
  Radar,
  Rocket,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  TimerReset,
  TrendingUp,
  Users,
  X,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { CREDIT_PACKS, PRICING_MATRIX, formatPrice } from '../../constants/pricing';
import type { TierId } from '../../constants/pricing';
import SEO from '../SEO';
import FreeTrafficClaimCard from './FreeTrafficClaimCard';
import './LandingRedesignPreview.css';

const previewPath = '/landing-redesign-preview';

const comparisonRows = [
  {
    icon: Users,
    feature: 'Campaign delivery model',
    trafficCreator: 'Configurable browser sessions',
    generic: 'Varies by provider',
    ads: 'Audience impressions and clicks',
  },
  {
    icon: BarChart3,
    feature: 'GA4 session visibility',
    trafficCreator: 'Designed in',
    generic: 'Varies',
    ads: 'Available with analytics setup',
  },
  {
    icon: Globe2,
    feature: 'Geo and device controls',
    trafficCreator: 'Tier-dependent controls',
    generic: 'Varies by provider',
    ads: 'Platform dependent',
  },
  {
    icon: Clock3,
    feature: 'Pacing and scheduling',
    trafficCreator: 'Included controls',
    generic: 'Varies',
    ads: 'Campaign scheduling',
  },
  {
    icon: Compass,
    feature: 'Automatic site crawler',
    trafficCreator: 'Expert tier option',
    generic: 'Varies by provider',
    ads: 'Landing-page based',
  },
  {
    icon: CircleDollarSign,
    feature: 'Buying model',
    trafficCreator: 'Pay per hit pack',
    generic: 'Varies',
    ads: 'Auction based',
  },
];

const benefits = [
  {
    icon: Activity,
    title: 'Analytics you can inspect.',
    copy: 'Review GA4-visible sessions, events, sources, and delivery logs instead of relying on vague traffic promises.',
    points: ['GA4 visibility', 'Live delivery review'],
  },
  {
    icon: SlidersHorizontal,
    title: 'Controls built for operators.',
    copy: 'Configure geography, device mix, source behavior, dwell time, bounce behavior, schedule, and pacing by tier.',
    points: ['Granular targeting', 'Flexible campaign setup'],
  },
  {
    icon: Zap,
    title: 'A faster campaign launch.',
    copy: 'Move from setup to an observable traffic test quickly, then adjust the campaign from one workspace.',
    points: ['Guided setup', 'Pause and edit controls'],
  },
];

const getPackDetails = (tier: TierId, volume: number) => {
  const label = CREDIT_PACKS.find((pack) => pack.volume === volume)?.label ?? `${volume.toLocaleString()} Hits`;
  const priceValue = PRICING_MATRIX[tier][volume][1];
  return {
    name: `${label.replace(/ Hits$/i, '')} Hit Pack`,
    price: formatPrice(priceValue),
    priceValue,
    volume: volume.toLocaleString('en-US'),
  };
};

interface PricingPlan {
  icon: LucideIcon;
  eyebrow: string;
  name: string;
  description: string;
  price: string;
  priceValue: number;
  volume: string;
  featured?: boolean;
  features: string[];
}

const pricingPlans: PricingPlan[] = [
  {
    ...getPackDetails('professional', 60000),
    icon: Rocket,
    eyebrow: 'Professional',
    description: 'A practical first paid campaign',
    features: [
      'Residential IP network',
      'GA4-visible campaign delivery',
      'Country-level targeting',
      'Credits never expire',
      'Pacing and schedule controls',
    ],
  },
  {
    ...getPackDetails('professional', 600000),
    icon: TrendingUp,
    eyebrow: 'Professional',
    description: 'More volume for ongoing tests',
    featured: true,
    features: [
      'Residential IP network',
      'GA4-visible campaign delivery',
      'Country-level targeting',
      'Credits never expire',
      'Pacing and schedule controls',
    ],
  },
  {
    ...getPackDetails('expert', 1000000),
    icon: Target,
    eyebrow: 'Expert',
    description: 'Premium controls at higher volume',
    features: [
      'Premium residential network',
      'GA4 natural events',
      'Premium targeting controls',
      'Automatic website crawler',
      'City-level precision targeting',
    ],
  },
];

const proofCards = [
  {
    icon: BadgeCheck,
    label: 'Analytics QA',
    copy: 'Confirm that configured sessions and engagement events appear in your GA4 property before you scale a test.',
    metric: 'GA4',
    metricLabel: 'Session visibility',
  },
  {
    icon: MapPin,
    label: 'Geo validation',
    copy: 'Compare country, region, city, and device delivery against the targeting profile selected for the campaign.',
    metric: 'Global',
    metricLabel: 'Coverage options',
  },
  {
    icon: Gauge,
    label: 'Delivery review',
    copy: 'Inspect pacing, scheduled windows, source mix, dwell behavior, and delivery progress from one dashboard.',
    metric: 'Live',
    metricLabel: 'Campaign monitoring',
  },
];

const faqItems = [
  {
    icon: Radar,
    question: 'What kind of traffic does Traffic Creator deliver?',
    answer:
      'Traffic Creator runs configurable, browser-simulated traffic campaigns using residential routing on supported tiers. It is designed for analytics testing, visibility analysis, and campaign validation—not as proof of real customer demand.',
  },
  {
    icon: BarChart3,
    question: 'Will the sessions appear in Google Analytics 4?',
    answer:
      'Campaign sessions and configured engagement events are designed to be visible in GA4. Your property, consent configuration, filters, and reporting setup can affect what you see, so every campaign should be verified in your own analytics.',
  },
  {
    icon: MapPin,
    question: 'Which geo-targeting options are available?',
    answer:
      'Traffic Creator offers broad global coverage. Available countries and targeting precision depend on the selected tier and supplier availability, so confirm current options in campaign setup.',
  },
  {
    icon: Zap,
    question: 'How quickly can a campaign launch?',
    answer:
      'Paid campaigns are designed for quick setup and launch. The free 6,000-session test begins after the domain check and WhatsApp verification have been completed.',
  },
  {
    icon: CreditCard,
    question: 'Do I need a credit card for the free test?',
    answer:
      'No. The free 24-hour test includes 6,000 sessions and does not require a credit card. One eligible public domain with GA4 or GTM installed, plus email and WhatsApp verification, is required before delivery starts.',
  },
  {
    icon: ShieldCheck,
    question: 'Are rankings, conversions, or platform treatment guaranteed?',
    answer:
      'No. Traffic Creator provides configurable traffic delivery and measurement controls. Rankings, conversions, AdSense treatment, and other platform outcomes depend on many factors and are not assured.',
  },
];

const productionUrl = 'https://traffic-creator.com/';

export const landingRedesignSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${productionUrl}#organization`,
      name: 'Traffic Creator',
      legalName: 'EasyTrafficBot UG (haftungsbeschränkt)',
      url: productionUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${productionUrl}favicon.svg`,
      },
      brand: {
        '@type': 'Brand',
        name: 'Traffic Creator',
      },
    },
    {
      '@type': 'WebSite',
      '@id': `${productionUrl}#website`,
      url: productionUrl,
      name: 'Traffic Creator',
      publisher: { '@id': `${productionUrl}#organization` },
      inLanguage: 'en',
    },
    {
      '@type': 'WebPage',
      '@id': `${productionUrl}#webpage`,
      url: productionUrl,
      name: 'Traffic Creator — Website Traffic Generator for SEO & Analytics',
      description: 'Website traffic generator for SEO and analytics: run geo-targeted, GA4-visible campaigns for validation, visibility research, and controlled experiments.',
      isPartOf: { '@id': `${productionUrl}#website` },
      about: { '@id': `${productionUrl}#application` },
      mainEntity: [
        { '@id': `${productionUrl}#service` },
        { '@id': `${productionUrl}#faq` },
      ],
      inLanguage: 'en',
    },
    {
      '@type': 'WebApplication',
      '@id': `${productionUrl}#application`,
      name: 'Traffic Creator',
      url: productionUrl,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      publisher: { '@id': `${productionUrl}#organization` },
      description: 'A browser-based workspace for configuring, pacing, and reviewing GA4-visible website traffic campaigns.',
      featureList: [
        'GA4-visible campaign delivery',
        'Geo and device targeting controls',
        'Campaign pacing and scheduling',
        'Delivery monitoring',
        'Automatic website crawler on supported tiers',
      ],
    },
    {
      '@type': 'Service',
      '@id': `${productionUrl}#service`,
      name: 'Configurable website traffic campaigns',
      serviceType: 'Website traffic campaign delivery and analytics testing',
      provider: { '@id': `${productionUrl}#organization` },
      areaServed: 'Worldwide',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Traffic Creator hit packs',
        itemListElement: [
          {
            '@type': 'Offer',
            name: '6,000-session free test',
            price: '0.00',
            priceCurrency: 'USD',
            url: `${productionUrl}#free-test`,
            description: 'A 24-hour test for one eligible public domain with GA4 or GTM installed, after email and WhatsApp verification.',
          },
          ...pricingPlans.map((plan) => ({
            '@type': 'Offer',
            name: `${plan.eyebrow} ${plan.name}`,
            price: plan.priceValue.toFixed(2),
            priceCurrency: 'USD',
            url: `${productionUrl}signup`,
            description: `${plan.volume} hits with ${plan.eyebrow.toLowerCase()} campaign controls.`,
          })),
        ],
      },
    },
    {
      '@type': 'FAQPage',
      '@id': `${productionUrl}#faq`,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ],
};

const LogoMark: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <span className={`tcr-logo-mark${compact ? ' tcr-logo-mark--compact' : ''}`} aria-hidden="true">
    <Bot size={compact ? 20 : 26} strokeWidth={2.4} />
  </span>
);

const SectionHeading: React.FC<{
  icon: React.ReactNode;
  eyebrow: string;
  title: React.ReactNode;
  copy: string;
}> = ({ icon, eyebrow, title, copy }) => (
  <div className="tcr-section-heading">
    <div className="tcr-eyebrow">
      {icon}
      <span>{eyebrow}</span>
    </div>
    <h2>{title}</h2>
    <p>{copy}</p>
  </div>
);

const RealDashboardMockup: React.FC = () => (
  <figure className="tcr-real-dashboard">
    <div className="tcr-real-dashboard-browser" aria-hidden="true">
      <div className="tcr-browser-dots"><i /><i /><i /></div>
      <div className="tcr-browser-address">
        <ShieldCheck size={12} />
        <span>traffic-creator.com/dashboard</span>
      </div>
      <div className="tcr-browser-label"><span /> Real product UI</div>
    </div>

    <div className="tcr-real-dashboard-media">
      <img
        src="/helpdesk/media/dashboard/current-dashboard-overview.webp?v=20260716-landing-mockup"
        alt="Traffic Creator demo dashboard with active campaigns and 30-day delivery analytics"
        width="1732"
        height="908"
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />
      <div className="tcr-real-dashboard-stamp" aria-hidden="true">
        <BadgeCheck size={15} /> Actual in-app interface
      </div>
    </div>

    <figcaption>
      <span><strong>Current Traffic Creator dashboard</strong> · First-party product capture</span>
      <small>Demo workspace and illustrative account data</small>
    </figcaption>
  </figure>
);

interface LandingRedesignPageProps {
  previewMode?: boolean;
}

const LandingRedesignPage: React.FC<LandingRedesignPageProps> = ({ previewMode = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="tcr-redesign" data-landing-design="redesign-home">
      <SEO
        title="Traffic Creator — Website Traffic Generator for SEO & Analytics"
        description="Website traffic generator for SEO and analytics: run geo-targeted, GA4-visible campaigns for validation, visibility research, and controlled experiments."
        keywords="website traffic generator, traffic generator, website traffic campaigns, GA4 traffic testing, geo targeted website traffic"
        image="/og-landing-new.jpg"
        schema={landingRedesignSchema}
        noindex={previewMode}
      />
      <a className="tcr-skip-link" href="#main-content">Skip to main content</a>

      <header className="tcr-header">
        <div className="tcr-shell tcr-header-inner">
          <a className="tcr-brand" href="#top" aria-label="Traffic Creator home">
            <LogoMark />
            <span>Traffic <strong>Creator</strong></span>
          </a>

          <nav className="tcr-desktop-nav" aria-label="Primary navigation">
            <a href="#control">Features</a>
            <a href="#workflow">Use cases</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <Link to="/blog">Resources</Link>
          </nav>

          <div className="tcr-header-actions">
            <Link className="tcr-login-link" to="/login">Log in</Link>
            <a className="tcr-button tcr-button--primary tcr-button--header" href="#free-test">
              View free test <ArrowRight size={17} />
            </a>
            <button
              className="tcr-menu-button"
              type="button"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((current) => !current)}
            >
              {mobileMenuOpen ? <X size={23} /> : <Menu size={23} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav className="tcr-mobile-nav" aria-label="Mobile primary navigation">
            <a href="#control" onClick={closeMobileMenu}>Features</a>
            <a href="#workflow" onClick={closeMobileMenu}>Use cases</a>
            <a href="#pricing" onClick={closeMobileMenu}>Pricing</a>
            <a href="#faq" onClick={closeMobileMenu}>FAQ</a>
            <Link to="/blog" onClick={closeMobileMenu}>Resources</Link>
            <Link to="/login" onClick={closeMobileMenu}>Log in</Link>
            <a href="#free-test" onClick={closeMobileMenu}>View free test</a>
          </nav>
        )}
      </header>

      <main id="main-content">
        <section className="tcr-hero" id="top">
          <div className="tcr-dot-field tcr-dot-field--hero" aria-hidden="true" />
          <div className="tcr-shell tcr-hero-grid">
            <div className="tcr-hero-copy">
              <div className="tcr-eyebrow">
                <Sparkles size={16} />
                <span>Configurable traffic · measurable workflows</span>
              </div>
              <h1>
                More traffic.<br />
                Clearer data.<br />
                <span className="tcr-accent-underline">Smarter decisions.</span>
              </h1>
              <p className="tcr-hero-lede">
                Run geo-targeted, GA4-visible website traffic campaigns for analytics validation,
                visibility research, and controlled experiments—all from one workspace.
              </p>

              <div className="tcr-hero-actions">
                <a className="tcr-button tcr-button--primary" href="#free-test">
                  View the free test <ArrowRight size={19} />
                </a>
                <a className="tcr-button tcr-button--secondary" href="#control">
                  <Play size={17} /> See how it works
                </a>
              </div>

              <div className="tcr-assurance-row" aria-label="Free test details">
                <span><Check size={16} /> 6,000 free sessions</span>
                <span><Check size={16} /> No credit card</span>
                <span><Check size={16} /> 24-hour test</span>
              </div>

              <div className="tcr-hero-capabilities">
                {[
                  { icon: Globe2, value: 'Global', label: 'coverage options' },
                  { icon: BadgeCheck, value: 'GA4', label: 'visible sessions' },
                  { icon: MousePointer2, value: 'Geo + device', label: 'targeting controls' },
                  { icon: TimerReset, value: 'Flexible', label: 'delivery pacing' },
                ].map((item) => (
                  <div key={item.label}>
                    <span className="tcr-capability-icon"><item.icon size={19} /></span>
                    <strong>{item.value}</strong>
                    <small>{item.label}</small>
                  </div>
                ))}
              </div>
            </div>

            <div className="tcr-hero-visual">
              <RealDashboardMockup />
            </div>
          </div>

          <div className="tcr-hero-wave tcr-hero-wave--one" aria-hidden="true" />
          <div className="tcr-hero-wave tcr-hero-wave--two" aria-hidden="true" />
        </section>

        <section className="tcr-audience-strip" aria-label="Traffic Creator use cases">
          <div className="tcr-section-shell">
            <p>Built for teams that need traffic they can configure and measure</p>
            <div className="tcr-audience-list">
              {['SEO teams', 'Growth agencies', 'Analytics QA', 'E-commerce', 'Local business', 'SaaS teams'].map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="tcr-section tcr-comparison-section" id="control">
          <div className="tcr-dot-field tcr-dot-field--left" aria-hidden="true" />
          <div className="tcr-section-shell">
            <SectionHeading
              icon={<SlidersHorizontal size={16} />}
              eyebrow="A more controlled way to test"
              title={<>Why Traffic Creator fits <span className="tcr-accent-underline">measurable workflows.</span></>}
              copy="Choose the campaign settings you need, observe what was delivered, and keep expectations grounded in data—not guaranteed outcomes."
            />

            <div className="tcr-comparison-scroll" tabIndex={0} aria-label="Scrollable product comparison">
              <table className="tcr-comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className="is-highlighted"><span><LogoMark compact /> Traffic Creator</span></th>
                    <th><span><Bot size={20} /> Basic traffic tools</span></th>
                    <th><span><Radar size={20} /> Traditional ads</span></th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.feature}>
                      <th scope="row"><row.icon size={18} /><span>{row.feature}</span></th>
                      <td className="is-highlighted"><Check size={17} /><span>{row.trafficCreator}</span></td>
                      <td><span>{row.generic}</span></td>
                      <td><span>{row.ads}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="tcr-comparison-note">Generalized comparison for planning only. Exact capabilities vary by provider and advertising platform.</p>

            <div className="tcr-benefit-grid">
              {benefits.map((benefit) => (
                <article className="tcr-benefit-card" key={benefit.title}>
                  <div className="tcr-card-icon"><benefit.icon size={29} /></div>
                  <div>
                    <h3>{benefit.title}</h3>
                    <p>{benefit.copy}</p>
                  </div>
                  <div className="tcr-card-points">
                    {benefit.points.map((point) => <span key={point}><Check size={15} /> {point}</span>)}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="tcr-section tcr-pricing-section" id="pricing">
          <div className="tcr-dot-field tcr-dot-field--right" aria-hidden="true" />
          <div className="tcr-section-shell">
            <SectionHeading
              icon={<Zap size={16} />}
              eyebrow="Pay once · choose your volume"
              title={<>Straightforward pricing for <span className="tcr-accent-underline">every test size.</span></>}
              copy="Start with a one-time hit pack, then choose the targeting profile and controls that fit your campaign."
            />

            <div className="tcr-pricing-grid">
              {pricingPlans.map((plan) => (
                <article className={`tcr-pricing-card${plan.featured ? ' is-featured' : ''}`} key={plan.name}>
                  {plan.featured && <div className="tcr-featured-label"><Sparkles size={14} /> Most popular</div>}
                  <div className="tcr-plan-header">
                    <div className="tcr-card-icon"><plan.icon size={28} /></div>
                    <div>
                      <span>{plan.eyebrow}</span>
                      <h3>{plan.name}</h3>
                      <p>{plan.description}</p>
                    </div>
                  </div>
                  <div className="tcr-plan-price"><strong>{plan.price}</strong><span>one time</span></div>
                  <div className="tcr-plan-volume"><strong>{plan.volume}</strong><span>hits</span></div>
                  <ul>
                    {plan.features.map((feature) => <li key={feature}><Check size={16} /> {feature}</li>)}
                  </ul>
                  <Link className="tcr-button tcr-button--primary tcr-plan-button" to="/signup">
                    Create account <ArrowRight size={18} />
                  </Link>
                  <small className="tcr-plan-note">Try 6,000 sessions free before choosing a pack.</small>
                </article>
              ))}
            </div>

            <div className="tcr-pricing-assurances">
              {[
                { icon: CircleDollarSign, title: 'Credits never expire', copy: 'Unused credits stay in your account' },
                { icon: CreditCard, title: 'Free test', copy: 'No credit card required' },
                { icon: MapPin, title: 'Tiered targeting', copy: 'Country to city controls' },
                { icon: ShieldCheck, title: 'Shortfall remedy', copy: 'Terms apply after campaign end' },
              ].map((item) => (
                <div key={item.title}>
                  <span><item.icon size={21} /></span>
                  <p><strong>{item.title}</strong><small>{item.copy}</small></p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="tcr-section tcr-proof-section" id="workflow">
          <div className="tcr-dot-field tcr-dot-field--left-bottom" aria-hidden="true" />
          <div className="tcr-section-shell">
            <SectionHeading
              icon={<BadgeCheck size={16} />}
              eyebrow="Evidence over promises"
              title={<>Make every campaign <span className="tcr-accent-underline">inspectable.</span></>}
              copy="Traffic Creator is designed to help teams validate configuration and delivery. Outcomes beyond traffic delivery remain dependent on your site, offer, channel, and platform."
            />

            <div className="tcr-proof-layout">
              <div className="tcr-proof-cards">
                {proofCards.map((card) => (
                  <article className="tcr-proof-card" key={card.label}>
                    <div className="tcr-proof-icon"><card.icon size={22} /></div>
                    <span className="tcr-proof-label">{card.label}</span>
                    <p>{card.copy}</p>
                    <div className="tcr-proof-metric">
                      <strong>{card.metric}</strong>
                      <span>{card.metricLabel}</span>
                    </div>
                  </article>
                ))}
              </div>

              <article className="tcr-case-card">
                <div className="tcr-case-topline">
                  <span><Target size={17} /> Example campaign</span>
                  <small>Illustrative configuration</small>
                </div>
                <h3>A 30-day analytics validation run</h3>
                <p>See how a campaign can be structured before any claims are made about business or platform outcomes.</p>

                <div className="tcr-case-metrics">
                  <div><span>Planned hits</span><strong>60,000</strong><small>Professional pack</small></div>
                  <div><span>Schedule</span><strong>30 days</strong><small>Even pacing</small></div>
                  <div><span>Analytics</span><strong>GA4</strong><small>Events configured</small></div>
                </div>

                <div className="tcr-case-chart">
                  <div className="tcr-case-chart-heading">
                    <strong>Example delivery plan</strong>
                    <span><i /> Planned pace</span>
                  </div>
                  <div className="tcr-bars" aria-hidden="true">
                    {[28, 39, 34, 52, 47, 64, 59, 71, 78, 73, 86, 91].map((height, index) => (
                      <span key={index} style={{ height: `${height}%` }} />
                    ))}
                  </div>
                  <div className="tcr-case-axis"><span>Day 1</span><span>Day 10</span><span>Day 20</span><span>Day 30</span></div>
                </div>

                <div className="tcr-case-footer">
                  <ShieldCheck size={20} />
                  <span><strong>Measured responsibly.</strong> Rankings, conversions, AdSense treatment, and other platform outcomes are not guaranteed.</span>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="tcr-section tcr-faq-section" id="faq">
          <div className="tcr-dot-field tcr-dot-field--right-bottom" aria-hidden="true" />
          <div className="tcr-section-shell">
            <SectionHeading
              icon={<Search size={16} />}
              eyebrow="Questions? Start here"
              title={<>Frequently asked <span className="tcr-accent-underline">questions.</span></>}
              copy="The practical details behind Traffic Creator campaigns, targeting, analytics visibility, and the free test."
            />

            <div className="tcr-faq-grid">
              {faqItems.map((item, index) => {
                const isOpen = openFaq === index;
                const Icon = item.icon;
                return (
                  <article className={`tcr-faq-item${isOpen ? ' is-open' : ''}`} key={item.question}>
                    <button
                      type="button"
                      id={`tcr-faq-button-${index}`}
                      aria-expanded={isOpen}
                      aria-controls={`tcr-faq-panel-${index}`}
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                    >
                      <span className="tcr-faq-icon"><Icon size={24} /></span>
                      <span className="tcr-faq-question">{item.question}</span>
                      <ChevronDown className="tcr-faq-chevron" size={21} />
                    </button>
                    <div
                      className="tcr-faq-answer"
                      id={`tcr-faq-panel-${index}`}
                      role="region"
                      aria-labelledby={`tcr-faq-button-${index}`}
                      hidden={!isOpen}
                    >
                      <p>{item.answer}</p>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="tcr-final-cta" id="free-test">
              <div className="tcr-cta-medallion"><TrendingUp size={35} /></div>
              <div className="tcr-cta-copy">
                <h2>Ready to test traffic with <span>clearer controls?</span></h2>
                <p>One eligible public domain with GA4 or GTM installed, plus email and WhatsApp verification, is required for the free 6,000-session test.</p>
              </div>
              <div className="tcr-cta-actions">
                <div>
                  <a className="tcr-button tcr-button--primary" href="#tcr-free-test-form">Start free test <ArrowRight size={18} /></a>
                  <a className="tcr-button tcr-button--secondary" href="#pricing">View pricing</a>
                </div>
                <p><span><Check size={14} /> No card</span><span><Check size={14} /> 24 hours</span><span><Check size={14} /> 6,000 sessions</span></p>
              </div>
              <div className="tcr-cta-wave" aria-hidden="true" />
            </div>
            <FreeTrafficClaimCard />
          </div>
        </section>
      </main>

      <footer className="tcr-footer">
        <div className="tcr-section-shell tcr-footer-grid">
          <div className="tcr-footer-brand">
            <a className="tcr-brand" href="#top" aria-label="Back to top">
              <LogoMark />
              <span>Traffic <strong>Creator</strong></span>
            </a>
            <p>Configurable, analytics-visible traffic campaigns for teams that want to test, measure, and learn.</p>
            <small>© 2026 EasyTrafficBot UG (haftungsbeschränkt)<br />Amtsgericht Wuppertal, HRB 30863</small>
          </div>

          <div className="tcr-footer-links">
            <div>
              <strong>Product</strong>
              <a href="#control">Features</a>
              <a href="#pricing">Pricing</a>
              <Link to="/website-traffic-generator">Traffic generator</Link>
              <Link to="/white-label-traffic-api">Traffic API</Link>
            </div>
            <div>
              <strong>Company</strong>
              <Link to="/about">About us</Link>
              <Link to="/blog">Blog</Link>
              <Link to="/helpdesk">Help center</Link>
            </div>
            <div>
              <strong>Resources</strong>
              <Link to="/buy-organic-traffic">Organic traffic</Link>
              <Link to="/buy-seo-traffic">SEO traffic</Link>
              <Link to="/ctr-manipulation-tool">CTR analysis</Link>
              <Link to="/high-quality-website-traffic">Traffic quality</Link>
            </div>
            <div>
              <strong>Legal</strong>
              <Link to="/legal/terms">Terms of service</Link>
              <Link to="/legal/privacy">Privacy policy</Link>
              <Link to="/legal/refund">Refund policy</Link>
              <Link to="/legal/impressum">Impressum</Link>
            </div>
          </div>
        </div>
        {previewMode && <div className="tcr-preview-note">Local design preview · {previewPath}</div>}
      </footer>
    </div>
  );
};

export default LandingRedesignPage;
